import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { getStatusTone, TONE_MARK } from '../shared/orderStatus';

/*
 * Status picker built on the Radix select already in the kit.
 *
 * Replaces react-select, which mounted a full instance per table row — each one
 * portalling to document.body — so a thirty-line order carried thirty-one of
 * them. Radix renders a plain trigger until it is opened.
 *
 * Radix requires string values; the order API speaks integers, so conversion is
 * contained here rather than leaking into every caller.
 */
const StatusSelect = ({
  value,
  options,
  onChange,
  disabled = false,
  pending = false,
  size = 'md',
  ariaLabel = 'Order status',
}) => {
  const stringValue = value == null ? '' : String(value);

  const handleChange = (next) => {
    const numeric = Number(next);
    if (Number.isNaN(numeric) || numeric === Number(value)) return;
    onChange(numeric);
  };

  const trigger =
    size === 'sm'
      ? 'h-7 text-[11.5px] px-2 gap-1.5 rounded-md'
      : 'h-9 text-[12.5px] px-2.5 rounded-lg';

  return (
    <Select
      value={stringValue}
      onValueChange={handleChange}
      disabled={disabled || pending}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className={`w-auto min-w-[124px] border-[#E1E7EA] bg-white text-nysmah-dark shadow-none focus:ring-nysmah-accent ${trigger}`}
      >
        {pending ? (
          <span className="flex items-center gap-1.5 text-nysmah-muted">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
            Saving…
          </span>
        ) : (
          <SelectValue placeholder="Set status" />
        )}
      </SelectTrigger>
      <SelectContent className="min-w-[168px]">
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={String(option.value)}
            disabled={option.disabled}
            className="text-[12.5px]"
          >
            <span className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: TONE_MARK[getStatusTone(option.value)] }}
                aria-hidden="true"
              />
              {option.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default StatusSelect;
